import { useState, FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/auth'
import cbcIcon from '../assets/logo/cbc-08.svg'

export default function Login() {
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()
  const { login } = useAuth()
  const env = import.meta.env as Record<string, string | undefined>
  const entraConfigured = Boolean(env.VITE_ENTRA_CLIENT_ID?.trim() && env.VITE_ENTRA_TENANT_ID?.trim())

  const getErrorMessage = (err: unknown) => {
    const message = err && typeof err === 'object' && 'message' in err && typeof (err as { message: unknown }).message === 'string'
      ? (err as { message: string }).message
      : ''
    const errorCode = err && typeof err === 'object' && 'errorCode' in err && typeof (err as { errorCode: unknown }).errorCode === 'string'
      ? (err as { errorCode: string }).errorCode
      : ''
    const errorDescription = err && typeof err === 'object' && 'errorMessage' in err && typeof (err as { errorMessage: unknown }).errorMessage === 'string'
      ? (err as { errorMessage: string }).errorMessage
      : err && typeof err === 'object' && 'errorDescription' in err && typeof (err as { errorDescription: unknown }).errorDescription === 'string'
        ? (err as { errorDescription: string }).errorDescription
        : ''

    const aadstsMatch = (message || errorDescription).match(/AADSTS\d+/)?.[0] || ''

    if (errorCode === 'user_cancelled') {
      return 'Inicio de sesión cancelado.'
    }

    if (errorCode === 'popup_window_error' || errorCode === 'popup_window_opened') {
      return 'El navegador bloqueó la ventana emergente. Habilita pop-ups para este sitio e intenta nuevamente.'
    }

    if (aadstsMatch === 'AADSTS500113') {
      return 'AADSTS500113: No hay un Reply URL registrado para esta aplicación. Agrega el Redirect URI exacto (origen actual) en la App Registration.'
    }

    if (aadstsMatch === 'AADSTS50011') {
      return 'AADSTS50011: El Redirect URI no coincide. Revisa que el Redirect URI registrado sea exactamente igual al origen donde estás probando.'
    }

    if (errorCode === 'hash_empty_error') {
      return 'No se pudo completar el proceso de autenticación. Recarga la página e intenta nuevamente.'
    }

    if (errorCode) {
      const details = (errorDescription || message).trim()
      if (details) {
        return `No se pudo iniciar sesión. Código: ${errorCode}. Detalle: ${details}`
      }
      return `No se pudo iniciar sesión. Código: ${errorCode}`
    }

    if (aadstsMatch) {
      return `No se pudo iniciar sesión. Código: ${aadstsMatch}`
    }

    return 'No se pudo iniciar sesión. Por favor, intenta de nuevo.'
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    try {
      const success = await login()
      if (success) {
        navigate('/', { replace: true })
      } else {
        setError(
          entraConfigured
            ? 'No se pudo iniciar sesión. Por favor, intenta de nuevo.'
            : 'Entra ID no está configurado. Define VITE_ENTRA_CLIENT_ID y VITE_ENTRA_TENANT_ID.',
        )
      }
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-cbc-navy relative overflow-hidden py-12 px-4">

      {/* Decorative background blobs */}
      <div className="absolute top-0 right-0 w-[550px] h-[550px] bg-cbc-primary/12 rounded-full blur-[130px] -translate-y-1/3 translate-x-1/4 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[420px] h-[420px] bg-cbc-medium-blue/12 rounded-full blur-[90px] translate-y-1/3 -translate-x-1/4 pointer-events-none" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(153,189,237,0.08),transparent_24%),linear-gradient(145deg,rgba(0,28,69,0.96),rgba(3,54,125,0.84))] pointer-events-none" />
      {/* Subtle diagonal line pattern */}
      <div
        className="absolute inset-0 opacity-[0.025] pointer-events-none"
        style={{
          backgroundImage:
            'repeating-linear-gradient(-45deg, #0369ff, #0369ff 1px, transparent 0, transparent 40px)',
        }}
      />

      {/* Card */}
      <div className="relative max-w-md w-full animate-fadeIn">
        <div className="relative rounded-3xl border border-cbc-medium-blue/20 bg-cbc-navy/70 backdrop-blur-2xl shadow-cbc-lg overflow-hidden">

          <div className="absolute top-6 right-6 hidden sm:flex items-center gap-2 rounded-2xl border border-cbc-light-blue/15 bg-cbc-light-blue/10 px-3 py-2">
            <div className="h-9 w-9 rounded-xl bg-cbc-paper/10 flex items-center justify-center">
              <img
                src={cbcIcon}
                alt="Icono CBC"
                className="h-5 w-5 brightness-0 invert opacity-95"
              />
            </div>
            <div className="text-left">
              <p className="text-[10px] font-semibold tracking-[0.24em] uppercase text-cbc-medium-blue/80">CBC</p>
              <p className="text-xs text-cbc-light-blue">Acceso seguro</p>
            </div>
          </div>

          {/* Card header */}
          <div className="relative bg-gradient-to-br from-cbc-navy via-cbc-dark-blue to-cbc-primary px-8 pt-10 pb-8 text-center overflow-hidden border-b border-cbc-light-blue/10">
            <div className="absolute -right-8 -top-8 w-36 h-36 bg-cbc-light-blue/10 rounded-full" />
            <div className="absolute right-6 bottom-0 w-20 h-20 bg-cbc-primary/20 rounded-full blur-xl" />
            <div className="absolute -left-4 bottom-2 w-16 h-16 bg-cbc-medium-blue/10 rounded-full blur-lg" />
            <div className="relative z-10">
              <img
                src={cbcIcon}
                alt="CBC Logo"
                className="h-14 w-14 mx-auto mb-5 brightness-0 invert"
              />
              <h2 className="text-2xl font-bold text-cbc-paper tracking-tight">Bienvenido</h2>
              <p className="mt-1 text-sm text-cbc-light-blue/85">Plataforma de Control de Gestión</p>
              <p className="text-[11px] text-cbc-medium-blue/70 mt-0.5 font-medium tracking-widest uppercase">
                Corporación de Bienes de Capital
              </p>
            </div>
          </div>

          {/* Form */}
          <div className="px-8 py-8 bg-gradient-to-b from-cbc-paper/[0.04] to-transparent">
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="bg-cbc-primary/12 border border-cbc-primary/30 text-cbc-paper px-4 py-3 rounded-xl text-sm flex items-center space-x-2">
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{error}</span>
                </div>
              )}

              <div className="rounded-2xl border border-cbc-medium-blue/20 bg-cbc-paper/[0.04] px-4 py-4">
                <p className="text-sm font-semibold text-cbc-paper">Acceso con Microsoft Entra ID</p>
                <p className="mt-1 text-xs text-cbc-light-blue/80">
                  Inicia sesión con tu cuenta corporativa para acceder a la plataforma.
                </p>
                {!entraConfigured && (
                  <p className="mt-2 text-[11px] text-cbc-medium-blue/80">
                    Faltan variables de entorno: VITE_ENTRA_CLIENT_ID y VITE_ENTRA_TENANT_ID.
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex items-center justify-center py-3 px-4 rounded-xl text-sm font-semibold text-cbc-paper bg-gradient-to-r from-cbc-primary to-cbc-dark-blue hover:from-cbc-dark-blue hover:to-cbc-navy shadow-cbc hover:shadow-cbc-lg focus:outline-none focus:ring-2 focus:ring-cbc-primary/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
              >
                {isLoading ? (
                  <span className="flex items-center space-x-2">
                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    <span>Iniciando sesión...</span>
                  </span>
                ) : (
                  'Ingresar con Microsoft'
                )}
              </button>
            </form>
          </div>

        </div>
      </div>
    </div>
  )
}
